import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './StageChangeDialog.css';

/**
 * Stage-change accountability dialog (sales rework Phase 12): raised when a
 * stage move answers 400 STAGE_CHANGE_DETAILS_REQUIRED. Asks whether the
 * stage's work was complete (notes MANDATORY when it wasn't), shows what the
 * move passes over and whether it wins the deal (absorbing the old won
 * confirm), and optionally hands the line item to a person or a group.
 * Nothing here blocks a move — it makes the move answerable.
 *
 * details: { from_stage, to_stage, passed_over[], is_won, open_task_ref }
 */
function StageChangeDialog({ stageName, lineLabel, details, onCancel, onSubmit, submitting, submitError }) {
  const { authFetch } = useAuth();
  const [taskComplete, setTaskComplete] = useState(null); // explicit choice, no default
  const [notes, setNotes] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [assignType, setAssignType] = useState('user');
  const [assignedToId, setAssignedToId] = useState('');
  const [assignedGroupId, setAssignedGroupId] = useState('');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!reassignOpen || users.length > 0) return;
    let cancelled = false;
    Promise.all([
      authFetch('/api/v1/opportunities/assignable-users').then(r => r.json()).catch(() => ({ data: [] })),
      authFetch('/api/v1/groups?purpose=operational').then(r => r.json()).catch(() => ({ data: [] }))
    ]).then(([usersData, groupsData]) => {
      if (cancelled) return;
      setUsers(usersData.data || []);
      setGroups(groupsData.data || []);
    });
    return () => { cancelled = true; };
  }, [reassignOpen, users.length, authFetch]);

  const handleSubmit = () => {
    setLocalError('');
    if (taskComplete === null) {
      setLocalError('Confirm whether the work for this stage was complete');
      return;
    }
    if (taskComplete === false && !notes.trim()) {
      setLocalError('Notes are required when the stage work was not complete');
      return;
    }
    if (reassignOpen && assignType === 'user' && !assignedToId) {
      setLocalError('Choose who to reassign to (or collapse the reassign section)');
      return;
    }
    if (reassignOpen && assignType === 'group' && !assignedGroupId) {
      setLocalError('Choose a group to reassign to (or collapse the reassign section)');
      return;
    }
    onSubmit({
      task_complete: taskComplete,
      notes: notes.trim() || null,
      ...(reassignOpen ? {
        reassign_to: assignType === 'user'
          ? { owner_id: assignedToId }
          : { owner_group_id: assignedGroupId }
      } : {})
    });
  };

  const passedOver = details?.passed_over || [];

  return (
    <div className="modal-overlay">
      <div className="modal-content stage-change-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Move to "{stageName}"</h2>
          <button type="button" className="modal-close" onClick={onCancel} disabled={submitting}>&times;</button>
        </div>
        <div className="modal-body">
          <p className="stage-change-intro">
            {lineLabel && <strong>{lineLabel}</strong>}
            {details?.from_stage && <> — from <strong>"{details.from_stage}"</strong></>}
          </p>

          {details?.is_won && (
            <div className="stage-change-callout won">
              This marks the line item <strong>WON</strong> — the asset record is generated and a prospect or dormant customer becomes Active automatically.
            </div>
          )}
          {passedOver.length > 0 && (
            <div className="stage-change-callout passed">
              This move passes over: <strong>{passedOver.join(', ')}</strong>
            </div>
          )}
          {details?.open_task_ref && (
            <div className="stage-change-callout task">
              The scheduled task <strong>{details.open_task_ref}</strong> for the current stage is still open.
            </div>
          )}

          {(localError || submitError) && <div className="modal-error">{localError || submitError}</div>}

          <div className="form-group">
            <label>Was the work for this stage complete? *</label>
            <div className="stage-complete-toggle">
              <label className="radio-option">
                <input type="radio" checked={taskComplete === true} onChange={() => setTaskComplete(true)} />
                Yes
              </label>
              <label className="radio-option">
                <input type="radio" checked={taskComplete === false} onChange={() => setTaskComplete(false)} />
                No
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="sc-notes">Notes{taskComplete === false ? ' *' : ''}</label>
            <textarea
              id="sc-notes"
              rows="2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={taskComplete === false ? 'Why is the line item moving with the work incomplete?' : 'Anything worth recording about this move...'}
            />
          </div>

          <div className="form-group">
            {!reassignOpen ? (
              <button type="button" className="btn-link reassign-toggle" onClick={() => setReassignOpen(true)}>
                + Reassign to a person or group with this move
              </button>
            ) : (
              <>
                <label>Reassign to <button type="button" className="btn-link" onClick={() => setReassignOpen(false)}>(keep current owner)</button></label>
                <div className="stage-complete-toggle">
                  <label className="radio-option">
                    <input type="radio" checked={assignType === 'user'} onChange={() => setAssignType('user')} />
                    A person
                  </label>
                  <label className="radio-option">
                    <input type="radio" checked={assignType === 'group'} onChange={() => setAssignType('group')} />
                    A group
                  </label>
                </div>
                {assignType === 'user' ? (
                  <select value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
                    <option value="">Select a team member...</option>
                    {users.map(user => (
                      <option key={user.user_id} value={user.user_id}>{user.full_name}{user.role ? ` (${user.role})` : ''}</option>
                    ))}
                  </select>
                ) : (
                  <select value={assignedGroupId} onChange={(e) => setAssignedGroupId(e.target.value)}>
                    <option value="">Select a group...</option>
                    {groups.map(group => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.name}{Number(group.member_count) >= 0 ? ` (${group.member_count} members)` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Moving...' : 'Move Stage'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StageChangeDialog;
